/**
 * 13 Shorts: the "Stories" grid block. A row of bubbles (one per episode), each
 * opening its cards (short vertical clips) in a full-screen viewer.
 *
 * Two layers:
 *   1. IStoriesBlock*  what the CMS sends (untrusted until parsed)
 *   2. IStory*         parsed: valid stories only, cards in order, ids as strings
 */

export interface IStoriesBlockImage {
  d?: string
  m?: string
  alt?: string
  lazy?: string
}

export interface IStoriesBlockVideo {
  duration?: number
  width?: number
  height?: number
  /** the id of this part, the key of the share link */
  id?: string | number
  kalturaId?: string
  name?: string
  onAirID?: string
  description?: string
}

/**
 * The contract nests the video and numbers the cards; the CMS
 * (feature/item-stories, StoryCard) sends the video fields on the card itself,
 * in order, without a number. Both are read.
 */
export interface IStoriesBlockCard extends IStoriesBlockVideo {
  storyCardOrder?: number
  video?: IStoriesBlockVideo
}

export interface IStoriesBlockPost {
  id?: string | number
  link?: string
  title?: string
  storyTitle?: string
  storyBoldTitle?: string
  storyCoverImage?: IStoriesBlockImage | null
  storyCards?: IStoriesBlockCard[]
  /** for analytics; null until the CMS fills them */
  episodeNumber?: number | string | null
  seriesId?: number | string | null
  /** also the full episode's ReferenceId in the analytics */
  seriesName?: string | null
}

export interface IStoriesBlock {
  grid_type: 'Stories'
  grid_id: number
  newsroom_name?: string
  posts: IStoriesBlockPost[]
  /** the ad numbers per platform, { desktop, mobileWeb } (storiesAds.ts) */
  ads?: unknown
  hideInApp?: boolean
}

export interface IStoryCard {
  /** "מספר קיטוע" in the CMS, 1-based */
  order: number
  /** the id of this part, the key of the share link; '' when the CMS has none */
  id: string
  kalturaId: string
  name: string
  duration: number
  onAirID: string
  /** the line under the clip (the text of this part) */
  description: string
}

export interface IStory {
  id: string
  link: string
  title: string
  /** "פרק 1:" - the bold part of the label, shown as sent */
  boldTitle: string
  storyTitle: string
  /** analytics only: "" while the CMS sends null */
  episodeNumber: number | ''
  seriesId: string
  seriesName: string
  cover: { d: string; m: string; alt: string }
  cards: IStoryCard[]
}

export const isStoriesBlock = (item: {
  grid_type?: string
}): item is IStoriesBlock =>
  item?.grid_type === 'Stories' &&
  Array.isArray((item as Partial<IStoriesBlock>).posts)
